// Pure logic for narrowing a set of raw-field duplicate-precheck
// matches down to the subset that counts as a "strong" match: same
// full name AND (same email OR same phone).
//
// Name alone isn't safe to act on (there are legitimately several
// John Smiths) and email/phone alone isn't either — spouses in this
// database legitimately share a household email or phone number, so
// refusing on that alone would break adding a second household
// member. Requiring BOTH is what makes a match safe to treat as "this
// is probably the same person" (server-side: refuse the create;
// client-side: show it as a stop, not just an FYI) rather than merely
// "worth a glance."
//
// Shared between the server (shared/db/contacts.ts, which uses this
// to refuse minting a twin household) and the client (the New
// Opportunity / New Contact forms, which use the same rule to decide
// whether a live-typing match is a hard stop or an informational
// note) so the two never disagree about what counts as "the same
// person."

export interface StrongDuplicateMatchInput {
  legal_first_name?: string | null;
  legal_last_name?: string | null;
}

export interface StrongDuplicateMatchCandidate {
  legal_first_name: string | null;
  legal_last_name: string | null;
  match_signals: readonly string[];
}

export function isStrongDuplicateMatch(
  input: StrongDuplicateMatchInput,
  candidate: StrongDuplicateMatchCandidate,
): boolean {
  const inputFirst = input.legal_first_name?.trim().toLowerCase();
  const inputLast = input.legal_last_name?.trim().toLowerCase();
  if (!inputFirst || !inputLast) return false;

  const hasContactSignal =
    candidate.match_signals.includes("email") ||
    candidate.match_signals.includes("phone");
  if (!hasContactSignal) return false;

  const candidateFirst = candidate.legal_first_name?.trim().toLowerCase();
  const candidateLast = candidate.legal_last_name?.trim().toLowerCase();
  return candidateFirst === inputFirst && candidateLast === inputLast;
}

export function findStrongDuplicateMatches<T extends StrongDuplicateMatchCandidate>(
  input: StrongDuplicateMatchInput,
  candidates: T[],
): T[] {
  return candidates.filter((c) => isStrongDuplicateMatch(input, c));
}
